import { useEffect, useMemo, useRef, useState } from "react"
import moment from 'moment'

import { IFinality } from './types'
import { IFinalityPrimaryList } from '@api/finality'

import { ComponentsHeader } from "@primitive/sectionHeadings"
import { Collapse } from 'antd'

import imgEmty from '@img/empty.svg'
import logoModSkill from '@img/logo_modskill.svg'
import logoYaSkill from '@img/logo_yaskill.svg'
import {
    IconApplications, IconCall,
    IconContracts, IconDelivery, IconEntity,
    IconListeners, IconEmail,
    IconContractClose,
    IconCopy
} from "@img/icons"

import { Button, Container, Empty, Flex, Loader, Pagination, ToolTip } from "@ui/index"

import css from './ui.module.scss'


/** Парсинг даты из строки DD.MM.YYYY */
const parseDate = (dateStr: string): Date | null => {
    if (!dateStr || dateStr.length !== 10) return null
    const [day, month, year] = dateStr.split('.')
    const date = new Date(Number(year), Number(month) - 1, Number(day))
    return isNaN(date.getTime()) ? null : date
}

/** Номер недели ISO 8601 */
const getWeekKey = (date: Date): string => {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
    const dayNum = d.getUTCDay() || 7
    d.setUTCDate(d.getUTCDate() + 4 - dayNum)
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1))
    const weekNum = Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7)
    return `${d.getUTCFullYear()}-W${String(weekNum).padStart(2, '0')}`
}

/** Пн и Пт недели по её ключу */
const getWeekRange = (weekKey: string): { monday: Date; friday: Date } | null => {
    const [, weekStr] = weekKey.split('-W')
    const week = parseInt(weekStr)
    if (isNaN(week)) return null
    const jan4 = new Date(parseInt(weekKey), 0, 4)
    const monday = new Date(jan4)
    monday.setDate(jan4.getDate() - (jan4.getDay() || 7) + 1 + (week - 1) * 7)
    const friday = new Date(monday)
    friday.setDate(monday.getDate() + 4)
    return { monday, friday }
}

/** Форматирование даты для заголовка */
const formatDateShort = (date: Date): string => {
    const months = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря']
    const weekdays = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб']
    return `${weekdays[date.getDay()]}, ${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`
}

/** Название месяца */
const formatMonthName = (monthKey: string): string => {
    const [year, month] = monthKey.split('-')
    const months = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь']
    return `${months[parseInt(month) - 1]} ${year}`
}

const getFinalitySortPriority = (item: IFinalityPrimaryList): number => {
    if (item.contractStatusID === 5) return 2
    if (item.isArchive === 1) return 1
    return 0
}

const getSiteLogo = (dataBase: string) => {
    if (dataBase === 'yaskill') return { src: logoYaSkill, alt: 'Логотип YaSkill' }
    if (dataBase === 'modern_academy') return { src: logoModSkill, alt: 'Логотип ModSkill' }
    return null
}

const getExpressChannelName = (dataBase: string) => dataBase === 'yaskill' ? 'Ya - Срочно' : 'ИКТ - Срочно'


/** Страница - Доставка */
export const Finality = ({
    isLoading, totalCount, pageSize, currentPageNumber,
    txtSearch, primaryList,
    txtSearchOnChange, loadPrimaryData, handleOpenCardDelivery, handlerOpenModalConfirm, handleCloseDelivery, handleCopyToClipboard,
    handleOpenCardCompany, handleOpenCardListener, handleOpenCardContract, handleOpenCardApplication, handleOpenModalSendPayment
}: IFinality) => {
    const formatter = new Intl.NumberFormat('ru-RU')

    /** Группировка списка по неделям */
    const weekGroups = useMemo(() => {
        const groups = new Map<string, { items: IFinalityPrimaryList[]; green: number; yellow: number; red: number; individuals: number; entities: number; priority: number }>()

        for (const item of primaryList) {
            const date = item.dateEducationEnd ? parseDate(item.dateEducationEnd) : null
            const key = date ? getWeekKey(date) : '__no_date__'
            if (!groups.has(key)) groups.set(key, { items: [], green: 0, yellow: 0, red: 0, individuals: 0, entities: 0, priority: getFinalitySortPriority(item) })
            const group = groups.get(key)!
            group.items.push(item)
            group.priority = Math.min(group.priority, getFinalitySortPriority(item))
            if (item.isDelivery === 1) group.green++
            else if (item.isPrint === 1 || item.isScan === 1) group.yellow++
            else if (item.contractStatusID === 6 || item.isArchive === 1) group.red++
            else group.yellow++
            if (item.contractTypeID === 2) group.entities++
            else group.individuals++
        }

        const entries = Array.from(groups.entries())
        entries.sort(([a], [b]) => {
            if (a === '__no_date__') return 1
            if (b === '__no_date__') return -1
            const priorityDiff = groups.get(a)!.priority - groups.get(b)!.priority
            if (priorityDiff !== 0) return priorityDiff
            return a.localeCompare(b)
        })

        entries.forEach(([, group]) => {
            group.items.sort((a, b) => {
                const priorityDiff = getFinalitySortPriority(a) - getFinalitySortPriority(b)
                if (priorityDiff !== 0) return priorityDiff

                const dateA = parseDate(a.dateEducationEnd)?.getTime() || 0
                const dateB = parseDate(b.dateEducationEnd)?.getTime() || 0
                if (dateA !== dateB) return dateA - dateB

                return a.contractListenerID - b.contractListenerID
            })
        })

        return entries
    }, [primaryList])

    /** Группировка недель по месяцам */
    const monthGroups = useMemo(() => {
        const groups = new Map<string, { items: typeof weekGroups; total: number; individuals: number; entities: number; priority: number }>()

        for (const [weekKey, group] of weekGroups) {
            let monthKey: string
            if (weekKey === '__no_date__') {
                monthKey = '__no_date__'
            } else {
                const range = getWeekRange(weekKey)
                if (!range) continue
                monthKey = `${range.monday.getFullYear()}-${String(range.monday.getMonth() + 1).padStart(2, '0')}`
            }
            if (!groups.has(monthKey)) groups.set(monthKey, { items: [], total: 0, individuals: 0, entities: 0, priority: group.priority })
            const mg = groups.get(monthKey)!
            mg.items.push([weekKey, group] as [string, typeof group])
            mg.total += group.items.length
            mg.individuals += group.individuals
            mg.entities += group.entities
            mg.priority = Math.min(mg.priority, group.priority)
        }

        return Array.from(groups.entries()).sort(([a], [b]) => {
            if (a === '__no_date__') return 1
            if (b === '__no_date__') return -1
            const priorityDiff = groups.get(a)!.priority - groups.get(b)!.priority
            if (priorityDiff !== 0) return priorityDiff
            return a.localeCompare(b)
        })
    }, [weekGroups])

    /** Ключ текущей недели */
    const currentWeekKey = useMemo(() => getWeekKey(new Date()), [])

    /** Ключ текущего месяца */
    const currentMonthKey = useMemo(() => {
        const now = new Date()
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
    }, [])

    /** Все ключи групп */
    const allWeekKeys = useMemo(() => weekGroups.map(([key]) => key), [weekGroups])
    const allMonthKeys = useMemo(() => monthGroups.map(([key]) => key), [monthGroups])

    /** Управляемое состояние активных панелей Collapse */
    const [activeKeys, setActiveKeys] = useState<string[]>([])
    const didInit = useRef(false)

    /** Первая загрузка: открыть текущий месяц и неделю */
    useEffect(() => {
        if (didInit.current) return
        if (weekGroups.length > 0) {
            didInit.current = true
            const keys: string[] = []
            if (monthGroups.some(([key]) => key === currentMonthKey)) keys.push(currentMonthKey)
            if (weekGroups.some(([key]) => key === currentWeekKey)) keys.push(currentWeekKey)
            setActiveKeys(keys)
        }
    }, [weekGroups, monthGroups, currentMonthKey, currentWeekKey])

    /** При изменении поиска или данных: раскрыть всё при поиске, свернуть до текущих при очистке */
    const prevSearch = useRef(txtSearch.value)
    useEffect(() => {
        if (!didInit.current) return

        const wasSearching = !!prevSearch.current
        const isSearching = !!txtSearch.value
        prevSearch.current = txtSearch.value

        if (isSearching) {
            // Поиск активен — раскрыть всё (в т.ч. при подгрузке новых данных)
            setActiveKeys([...allMonthKeys, ...allWeekKeys])
        } else if (wasSearching && !isSearching) {
            // Поиск только что очистили — свернуть до текущих
            const keys: string[] = []
            if (monthGroups.some(([key]) => key === currentMonthKey)) keys.push(currentMonthKey)
            if (weekGroups.some(([key]) => key === currentWeekKey)) keys.push(currentWeekKey)
            setActiveKeys(keys)
        }
        // Если не ищем и не было поиска — ничего не делаем, сохраняем ручное состояние
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [txtSearch.value, allMonthKeys, allWeekKeys, monthGroups, weekGroups, currentMonthKey, currentWeekKey])

    return <div className={css['container']}>

        <ComponentsHeader
            headerIcon={<IconDelivery width="20px" height="20px" />}
            headerTitle="Доставка"
            txtSearch={txtSearch.value}
            txtSearchTooltip={<>Поиск по:<br />✓ Номеру договора<br />✓ Номеру заявки<br />✓ Фамилии клиента<br />✓ Имени клиента<br />✓ Отчеству клиента<br />✓ Телефону клиента<br />✓ Email клиента<br />✓ Наименованию контрагента<br />✓ Адресу доставки</>}
            txtSearchOnChange={(e) => txtSearchOnChange(e.target.value)}
            txtSearchClearOnClick={() => txtSearchOnChange('')}
            btnUpdateOnClick={() => loadPrimaryData(txtSearch.value)}
        />

        <div className={css.loading_region}>
            <Loader isOpen={isLoading} backdrop color="var(--foreground_accent_default)" title="Загрузка данных..." />
            <div className='table_cards_body'>
            {!primaryList.length
                ? <Empty image={imgEmty} title='Данные отсутствуют' />
                : (
                    <Collapse
                        activeKey={activeKeys}
                        onChange={(keys) => setActiveKeys(typeof keys === 'string' ? [keys] : keys)}
                        className={css['date_collapse']}
                        expandIconPosition="end"
                        size="small"
                    >
                        {monthGroups.map(([monthKey, monthGroup]) => {
                            const isNoDate = monthKey === '__no_date__'
                            const formattedMonth = isNoDate ? 'Без даты' : formatMonthName(monthKey)
                            const isCurrentMonth = monthKey === currentMonthKey

                            return (
                                <Collapse.Panel
                                    key={monthKey}
                                    className={isCurrentMonth ? css['panel_closest'] : ''}
                                    header={
                                        <span className={css['collapse_header']}>
                                            <span className={`${css['collapse_date']} ${isCurrentMonth ? css['collapse_date_closest'] : ''}`}>{formattedMonth}</span>
                                            <span className={css['collapse_counts']}>
                                                {monthGroup.individuals > 0 && <span className={`${css['count_badge']} ${css['count_blue']}`}>{monthGroup.individuals}</span>}
                                                {monthGroup.entities > 0 && <span className={`${css['count_badge']} ${css['count_entity']}`}>{monthGroup.entities}</span>}
                                            </span>
                                        </span>
                                    }
                                >
                                    <Collapse
                                        activeKey={activeKeys}
                                        onChange={(keys) => setActiveKeys(typeof keys === 'string' ? [keys] : keys)}
                                        className={css['date_collapse_nested']}
                                        expandIconPosition="end"
                                        size="small"
                                    >
                                        {monthGroup.items.map(([weekKey, group]) => {
                                            const isWeekNoDate = weekKey === '__no_date__'
                                            const range = isWeekNoDate ? null : getWeekRange(weekKey)
                                            const formattedDate = isWeekNoDate ? 'Без даты' : range ? `${formatDateShort(range.monday)} 👉 ${formatDateShort(range.friday)}` : weekKey
                                            const isCurrentWeek = weekKey === currentWeekKey

                                            return (
                                                <Collapse.Panel
                                                    key={weekKey}
                                                    className={isCurrentWeek ? css['panel_closest'] : ''}
                                                    header={
                                                        <span className={css['collapse_header']}>
                                                            <span className={`${css['collapse_date']} ${isCurrentWeek ? css['collapse_date_closest'] : ''}`}>{formattedDate}</span>
                                                            <span className={css['collapse_counts']}>
                                                                {group.individuals > 0 && <span className={`${css['count_badge']} ${css['count_blue']}`}>{group.individuals}</span>}
                                                                {group.entities > 0 && <span className={`${css['count_badge']} ${css['count_entity']}`}>{group.entities}</span>}
                                                            </span>
                                                        </span>
                                                    }
                                                >
                                                    {(() => {
                                                        const individuals = group.items.filter(i => i.contractTypeID !== 2)
                                                        const entities = group.items.filter(i => i.contractTypeID === 2)

                                                        // Группировка юрлиц по договорам
                                                        const entityContracts = new Map<number, typeof group.items>()
                                                        for (const item of entities) {
                                                            if (!entityContracts.has(item.contractID)) entityContracts.set(item.contractID, [])
                                                            entityContracts.get(item.contractID)!.push(item)
                                                        }

                                                        const renderCard = (item: IFinalityPrimaryList) => {
                                                            const fullName = `${item.lastName} ${item.firstName || ''} ${item.secondName || ''}`.trim()
                                                            const siteLogo = getSiteLogo(item.dataBase)
                                                            const expressChannelName = getExpressChannelName(item.dataBase)
                                                            const debt = item.isCoursePaid === 1 ? 0 : (item.contractSum - item.contractPayment)
                                                            const hasDebt = debt > 0
                                                            const isActive = item.contractStatusID === 4
                                                            const isArchive = item.isArchive === 1
                                                            const isEntity = item.contractTypeID === 2
                                                            const endDateSoon = moment().add(10, 'days') > moment(item.dateEducationEnd, 'DD.MM.YYYY')

                                                            return (
                                                                <div key={item.contractListenerID} className={`${css['card']} ${isEntity ? css['card_entity'] : ''}`} onClick={() => handleOpenCardDelivery(true, item.contractID)}>
                                                                    <div className={css['card_top']}>
                                                                        <div className={css['card_name_row']}>
                                                                            <div className={css['card_name']}>
                                                                                {siteLogo && <img className={css['card_site_logo']} src={siteLogo.src} alt={siteLogo.alt} />}
                                                                                <span>{fullName}</span>
                                                                            </div>
                                                                            <div className={css['card_name_actions']}>
                                                                                {!isEntity && 
                                                                                    <ToolTip title='Закрыть договор' placement="bottomRight">
                                                                                        <Button variant="icon" size="sm" onClick={(e) => { handleCloseDelivery(item.contractID, item.contractNumber, item.trackNumber); e.stopPropagation() }}>
                                                                                            <IconContractClose width='18px' height='18px' />
                                                                                        </Button>
                                                                                    </ToolTip>
                                                                                }
                                                                                
                                                                                <ToolTip title='Скопировать данные' placement="bottomRight">
                                                                                    <Button variant="icon" size="sm" onClick={(e) => { handleCopyToClipboard({ contractNumber: item.contractNumber, applicationID: item.applicationID, contractTypeName: item.contractTypeName, lastName: item.lastName, firstName: item.firstName, secondName: item.secondName, phoneNumber: item.phoneNumber, email: item.email, deliveryToWhom: item.deliveryToWhom, deliveryAddress: item.deliveryAddress }); e.stopPropagation() }}>
                                                                                        <IconCopy width='18px' height='18px' />
                                                                                    </Button>
                                                                                </ToolTip>
                                                                            </div>
                                                                        </div>
                                                                        <div className={css['card_contacts']}>
                                                                            {item.phoneNumber && <span className={css['contact_item']}><IconCall width='14px' height='14px' />{item.phoneNumber}</span>}
                                                                            {item.email && <span className={css['contact_item']}><IconEmail width='14px' height='14px' />{item.email}</span>}
                                                                        </div>
                                                                    </div>
                                                                    {isArchive && <div className={css['archive_banner']}>АНКЕТА НАХОДИТСЯ В АРХИВЕ</div>}
                                                                    <div className={css['card_info']}>
                                                                        <div className={css['info_left']}>
                                                                            <div className={css['contract_num']}>№ {item.contractNumber}<span className={css['app_id']}> 👉 Заявка № {item.applicationID}</span></div>
                                                                            <div className={css['meta_row']}>
                                                                                <span className={`${css['badge']} ${isActive ? css['badge_active'] : ''} ${isEntity ? css['badge_entity'] : ''}`}>{item.contractTypeName}</span>
                                                                                <span className={css['meta_text']}>{item.contractStatusName}</span>
                                                                            </div>
                                                                            <div className={css['meta_row']}><span className={css['meta_label']} title={item.courseName}>{item.courseName}</span></div>
                                                                            <div className={css['meta_row']}>
                                                                                <span className={css['meta_text']}>{item.documentTypeName}</span>
                                                                                <span className={css['dot']}>·</span>
                                                                                <span className={css['meta_text']}>{item.attestationTypeName}</span>
                                                                            </div>
                                                                        </div>
                                                                        <div className={css['info_right']}>
                                                                            {hasDebt ? (
                                                                                <div className={css['debt_block']}><span className={css['debt_label']}>Долг</span><span className={css['debt_value']}>{formatter.format(debt)} ₽</span></div>
                                                                            ) : (
                                                                                <div className={css['paid_block']}>Оплачено</div>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                    <div className={css['date_row']}>
                                                                        <div className={css['date_dates']}>
                                                                            <span className={css['date_item']}>{item.dateEducationStart}</span>
                                                                            <span className={css['date_arrow']}>👉</span>
                                                                            <span className={`${css['date_item']} ${endDateSoon ? css['date_warn'] : ''}`}>{item.dateEducationEnd}</span>
                                                                        </div>
                                                                        <div className={css['date_track']}>
                                                                            {item.trackNumber
                                                                                ? <span className={css['track_number']}>📦 {item.trackNumber}</span>
                                                                                : <span className={css['track_missing']}>Трек отсутствует</span>
                                                                            }
                                                                        </div>
                                                                    </div>
                                                                    <div className={css['chips_row']}>
                                                                        <span className={`${css['chip']} ${item.isPostPayment === 1 ? css['chip_ok'] : css['chip_muted']}`}>Пост-оплата</span>
                                                                        <span className={`${css['chip']} ${item.isLearningOutsideMoodle === 1 ? css['chip_ok'] : css['chip_muted']}`}>Вне LMS</span>
                                                                        <span className={`${css['chip']} ${item.isDeliveryCDEK === 1 ? css['chip_ok'] : css['chip_muted']}`}>СДЭК</span>
                                                                        <span className={`${css['chip']} ${item.isAddressConfirmed === 1 ? css['chip_ok'] : css['chip_muted']}`}>Адрес</span>
                                                                        <span className={css['chips_sep']} />
                                                                        <span className={`${css['chip']} ${item.isFinalExamination === 1 ? css['chip_ok'] : css['chip_muted']}`}>Работа</span>
                                                                        <span className={`${css['chip']} ${item.isPrint === 1 ? css['chip_ok'] : css['chip_muted']}`}>Печать</span>
                                                                        <span className={`${css['chip']} ${item.isScan === 1 ? css['chip_ok'] : css['chip_muted']}`}>Скан</span>
                                                                        <span className={`${css['chip']} ${item.isDelivery === 1 ? css['chip_ok'] : css['chip_muted']}`}>Доставка</span>
                                                                    </div>
                                                                    <div className={css['card_actions']}>
                                                                        <div className={css['actions_icons']}>
                                                                            <ToolTip title='Анкета заявки' placement="top"><Button variant="icon" size="md" onClick={(e) => { handleOpenCardApplication(true, item.applicationID); e.stopPropagation() }}><IconApplications width='16px' height='16px' /></Button></ToolTip>
                                                                            <ToolTip title='Анкета договора' placement="top"><Button variant="icon" size="md" onClick={(e) => { handleOpenCardContract(true, item.contractID); e.stopPropagation() }}><IconContracts width='16px' height='16px' /></Button></ToolTip>
                                                                            <ToolTip title='Анкета слушателя' placement="top"><Button variant="icon" size="md" onClick={(e) => { handleOpenCardListener(true, item.listenerID); e.stopPropagation() }}><IconListeners width='16px' height='16px' /></Button></ToolTip>
                                                                            {!!item.companyID && <ToolTip title='Анкета организации' placement="top"><Button variant="icon" size="md" onClick={(e) => { handleOpenCardCompany(true, item.companyID); e.stopPropagation() }}><IconEntity width='16px' height='16px' /></Button></ToolTip>}
                                                                        </div>
                                                                        <div className={css['actions_btns']}>
                                                                            <Button padding='2px 8px' variant="primary" size="md" onClick={(e) => { handleOpenModalSendPayment(true, item.contractID, item.contractTypeID); e.stopPropagation() }}>Счёт</Button>
                                                                            <Button padding='2px 8px' variant="primaryDark" size="md" onClick={(e) => { handlerOpenModalConfirm(true, 'Отправка заявления', 'Вы действительно хотите отправить заявление на доставку?', 'sendingApplicationDeliveryToClient', item.contractID, expressChannelName); e.stopPropagation() }}>Заявление</Button>
                                                                            <Button padding='2px 8px' variant="primaryDark" size="md" onClick={(e) => { handlerOpenModalConfirm(true, 'Отправка заявления', 'Вы действительно хотите отправить итоговый счёт и заявление на доставку?', 'sendingContractStatementDelivery', item.contractID, expressChannelName); e.stopPropagation() }}>Счёт + Заявление</Button>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            )
                                                        }

                                                        return <Flex gap="10px" flexDirection="column">
                                                            {/* Физические лица */}
                                                            {individuals.length > 0 && (
                                                                <div className={css['sub_group']}>
                                                                    <div className={css['sub_group_header']}>
                                                                        <span className={css['sub_group_icon']}>👤</span>
                                                                        Физические лица
                                                                        <span className={css['sub_group_count']}>{individuals.length}</span>
                                                                    </div>
                                                                    <Flex gap="6px" flexDirection="row" flexWrap="wrap">
                                                                        {individuals.map(renderCard)}
                                                                    </Flex>
                                                                </div>
                                                            )}

                                                            {/* Юридические лица */}
                                                            {entities.length > 0 && (
                                                                <div className={css['sub_group']}>
                                                                    <div className={css['sub_group_header']}>
                                                                        <span className={css['sub_group_icon']}>🏢</span>
                                                                        Юридические лица
                                                                        <span className={`${css['sub_group_count']} ${css['sub_group_count_entity']}`}>{entities.length}</span>
                                                                    </div>
                                                                    <Flex gap="8px" flexDirection="column">
                                                                        {Array.from(entityContracts.entries()).map(([contractID, contractItems]) => (
                                                                            <div key={contractID} className={css['contract_group']}>
                                                                                <div className={css['contract_group_header']}>
                                                                                    <span className={css['contract_group_left']}>
                                                                                        <span>📋 Договор № {contractItems[0].contractNumber}</span>
                                                                                        <span className={css['contract_group_badge']}>{contractItems.length} слуш.</span>
                                                                                    </span>
                                                                                    <Button padding='2px 8px' variant="primaryDark" size="sm" onClick={(e) => { handleCloseDelivery(contractItems[0].contractID, contractItems[0].contractNumber, contractItems[0].trackNumber); e.stopPropagation() }}>Закрыть</Button>
                                                                                </div>
                                                                                <Flex gap="6px" flexDirection="row" flexWrap="wrap">
                                                                                    {contractItems.map(renderCard)}
                                                                                </Flex>
                                                                            </div>
                                                                        ))}
                                                                    </Flex>
                                                                </div>
                                                            )}
                                                        </Flex>
                                                    })()}
                                                </Collapse.Panel>
                                            )
                                        })}
                                    </Collapse>
                                </Collapse.Panel>
                            )
                        })}
                    </Collapse>
                )
            }
            </div>
        </div>

        <Container padding="6px" className={css['footer']} overflow="visible">
            <Flex gap="4px" flexDirection="row" alignItems="center">
                <ToolTip title='Общее количество' placement="topLeft">
                    <p className={css['stat_all']}>{totalCount}</p>
                </ToolTip>
            </Flex>

            <Pagination
                totalCount={totalCount}
                pageSize={pageSize}
                currentPage={currentPageNumber.value}
                onChange={(e) => currentPageNumber.setValue(e)}
            />
        </Container>
    </div>
}
